import { forwardRef, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { setAlpha } from '../../design-tokens';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { Button, ButtonVariant } from '../Button';
import { useDirectSpeech } from '../DirectSpeech';
import { Flow } from '../Layouts/Flow';
import { useNotifyClosing } from './__internal__';
import { BusyStateAnimationElement } from './BusyStateAnimationElement';
import bundle from './NovaComponents-BusyStateIndicator-gui-icu.properties';
import cn from './BusyStateIndicator.css.json';
import './BusyStateIndicator.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useTextId = createUseHtmlId('BusyStateIndicator-text');
const calculateDelay = delay => {
  if (delay === undefined || delay === null) return 300;
  return delay > 0 ? delay : 0;
};
export const BusyStateIndicator = /*#__PURE__*/forwardRef(function BusyStateIndicator(props, ref) {
  const localize = useTextLocalizer();
  const directSpeech = useDirectSpeech();
  const {
    text,
    buttonText,
    onButtonPress,
    active,
    children,
    testId,
    hideGraphic
  } = props;
  const textId = useTextId();
  const delay = calculateDelay(props.delay);
  const [timerFinished, setTimerFinished] = useState(false);
  const stateContainerRef = useRef(null);
  const childrenContainerRef = useRef(null);
  const busyStateAnimationRef = useRef(null);
  const textRef = useRef(null);
  const beforeChildrenRef = useRef(null);
  const prevFocusedElementRef = useRef(null);
  const ignoreOnFocusRef = useRef(false);
  const dt = useDesignTokens();
  const isBusyVisible = !!active && (timerFinished || delay === 0);
  const processedText = text ?? (hideGraphic ? localize('hideGraphicLoading.label') : text);
  const tokens = {
    ...getBusyStateIndicatorDesignTokens(dt, {
      tokens: props.tokens
    }),
    ...props.theme?.(dt)
  };
  useEffect(() => {
    if (!active) {
      setTimerFinished(false);
      return;
    }
    const timer = setTimeout(() => setTimerFinished(true), delay);
    return () => clearTimeout(timer);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeMsg = props.activeMsg ?? localize('active.aria');
  useEffect(() => {
    if (isBusyVisible) {
      if (!prevFocusedElementRef.current) {
        // Store the previously focused element so that it can be restored later
        prevFocusedElementRef.current = document.activeElement;
      }
      if (!props.disableAutoFocus && childrenContainerRef.current?.contains(document.activeElement)) {
        hideGraphic ? textRef.current?.focus() : busyStateAnimationRef.current?.focus();
      } else if (activeMsg) {
        // If the focus is not within BusyStateIndicator, notify users that the process has started
        directSpeech?.notify(activeMsg);
      }
    } else if (prevFocusedElementRef.current) {
      if (!props.disableAutoFocus) {
        // Restore the previously focused element after the busy state indicator is removed.
        prevFocusedElementRef.current?.focus?.();
      }
      prevFocusedElementRef.current = null;
    }
  }, [directSpeech, isBusyVisible, activeMsg, hideGraphic, props.disableAutoFocus]);
  const onActiveRef = useRef(props.onActive);
  onActiveRef.current = props.onActive;
  useEffect(() => {
    if (isBusyVisible) {
      onActiveRef.current?.();
    }
  }, [isBusyVisible]);
  useNotifyClosing({
    isOpen: isBusyVisible,
    message: props.dismissedMsg ?? localize('dismissed.aria')
  });
  if (!isBusyVisible && !stateContainerRef.current?.contains(document.activeElement)) {
    // Clear the previously focused element if the busy state indicator no longer has the focus
    prevFocusedElementRef.current = null;
  }
  const state = /*#__PURE__*/_jsxs(Flow, {
    className: cx(cn.state, props.className),
    style: {
      ...tokens,
      ...props.style
    },
    "data-testid": testId,
    ref: stateContainerRef,
    orientation: "vertical",
    children: [!hideGraphic ? /*#__PURE__*/_jsx(BusyStateAnimationElement, {
      ref: element => {
        setRef(props.busyStateAnimationRef, element);
        busyStateAnimationRef.current = element;
      },
      ariaLabel: props.ariaLabel,
      ariaDescribedBy: processedText ? textId : undefined,
      tokens: props.animationTokens
    }) : null, processedText ? /*#__PURE__*/_jsx("span", {
      ref: textRef,
      className: cn.stateText,
      id: textId,
      tabIndex: hideGraphic ? 0 : undefined,
      children: processedText
    }) : null, buttonText ? /*#__PURE__*/_jsx("div", {
      className: cn.buttonContainer,
      children: /*#__PURE__*/_jsx(Button, {
        variant: ButtonVariant.Small,
        onPress: onButtonPress,
        text: buttonText
      })
    }) : null]
  });
  if (!children) {
    return isBusyVisible ? state : null;
  }
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.container, props.fillParentContainer && cn.fillParentContainer, props.containerClassName),
    style: {
      ...tokens,
      ...props.containerStyle
    },
    ref: ref,
    children: [isBusyVisible ? /*#__PURE__*/_jsx("span", {
      tabIndex: 0,
      ref: beforeChildrenRef,
      onFocus: () => {
        if (!ignoreOnFocusRef.current) {
          hideGraphic ? textRef.current?.focus() : busyStateAnimationRef.current?.focus();
        }
      },
      "data-autofocus-inside": "true"
    }) : null, /*#__PURE__*/_jsx("div", {
      className: cn.childrenContainer,
      ref: childrenContainerRef,
      children: children
    }), isBusyVisible ? /*#__PURE__*/_jsx("div", {
      className: cx(cn.overlay, props.overlayClassName),
      style: props.overlayStyle,
      onKeyDown: e => {
        if (e.key === 'Tab' && e.shiftKey && e.target === (hideGraphic ? textRef.current : busyStateAnimationRef.current)) {
          // Redirect the focus to the element before the children
          ignoreOnFocusRef.current = true;
          beforeChildrenRef.current?.focus();
          ignoreOnFocusRef.current = false;
        }
      },
      children: state
    }) : null]
  });
});
export const getBusyStateIndicatorDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'overlay-bg': opts?.tokens?.().bg ?? setAlpha(dt.tokens['color-container-background-0'], 0.9),
      fg: opts?.tokens?.().fg ?? dt.tokens['color-body-text'],
      pd: dt.tokens['space-base-s']
    }, 'busy-state'),
    ...renderDesignTokens({
      bg: dt.tokens['color-container-background-0']
    }, 'button-container'),
    ...(!dt.enableNovaTokens && getBusyStateIndicatorLegacyDesignTokens(dt, opts))
  };
};
export const getBusyStateIndicatorLegacyDesignTokens = (dt, opts) => {
  let defaultBg = '';
  let defaultFg = '';
  switch (dt.themeVariant) {
    case 'dark':
      defaultBg = setAlpha(dt.c.p_2, 0.6);
      defaultFg = dt.c.white;
      break;
    case 'high-contrast':
      defaultBg = setAlpha(dt.c.black, 0.6);
      defaultFg = dt.c.white;
      break;
    default:
      defaultBg = setAlpha(dt.c.white, 0.8);
      defaultFg = dt.c.n_3;
  }
  return {
    ...renderDesignTokens({
      'font-size-m': dt.reportTokens.font.size.m,
      'overlay-bg': opts?.tokens?.().bg ?? defaultBg,
      fg: opts?.tokens?.().fg ?? defaultFg
    }, 'busy-state'),
    ...renderDesignTokens({
      bg: dt.reportTokens.backgrounds.panes.bg
    }, 'button-container')
  };
};
//# sourceMappingURL=BusyStateIndicator.js.map